<script lang="ts">
    import type * as t from "./types";

    import { derived } from "svelte/store";
    import { slide, fade, fly } from "svelte/transition";
    import store, { init as initStore } from "./quiz-store";
    import ProgressBullets from "./ProgressBullets.svelte";
    import ResponsiveImage from "./ResponsiveImage.svelte";
    import Segments from "./Segments.svelte";
    import { Sequence } from "./sequence";
    import ProgressPercentage from "./ProgressPercentage.svelte";
    import { getRelativeStepIdx } from "./utils";
    import LoadingDots from "./LoadingDots.svelte";

    export let ready: boolean;
    export let logo;
    export let coverImage;
    export let backgroundImage;
    export function setReady(isIt: boolean) {
        ready = isIt;
    }

    const segments = new Sequence<t.Segment>($$props.segments);
    const hasCoverImage = coverImage != null;
    const totalSteps = segments.reduce((acc, segment) => {
        return acc + segment.steps.length;
    }, 0);

    let innerWidth: number;

    initStore({
        config: $$props.config,
        segment: segments.head!,
        step: segments.head!.value.steps.head!,
        globalStepIdx: 0,
        score: {},
        totalSteps,
        klaviyoConfig: $$props.klaviyoConfig,
    });

    const segment = derived(store, ($s) => $s.segment.value);
    const step = derived(store, ($s) => $s.step.value);
    const stepIdx = derived(store, ($s) =>
        getRelativeStepIdx(segments, $s.globalStepIdx),
    );
    const segmentConfig = derived(
        [store, segment],
        ([$s, $segment]) => $s.config.segments[$segment.id],
    );
    const isCoverImageVisible = derived(
        step,
        ($step) => hasCoverImage && $step.type === "intro",
    );
    const isProgressVisible = derived(
        [segment, segmentConfig, step],
        ([$segment, $segmentConfig, $step], set) => {
            const visible = shouldShowProgress($segment, $segmentConfig, $step);

            let timeoutId: number | undefined;
            if (!visible) {
                timeoutId = setTimeout(() => {
                    set(visible);
                }, 750);
            } else {
                set(visible);
            }

            return () => {
                clearTimeout(timeoutId);
            };
        },
    );
    const marginLeftOffset = derived(store, ($s) => $s.globalStepIdx * 100);

    let isTitleVisible = false;
    $: isTitleVisible =
        $segment.id !== "fixed" &&
        $step.type !== "segment_result" &&
        !!$step.title;

    function shouldShowProgress(
        segment: t.Segment,
        segmentConfig: t.SegmentConfig | null,
        step: t.Step,
    ) {
        let should = true;
        should = should && segment.id !== "fixed";
        should = should && segmentConfig != null;
        should = should && step.type !== "segment_result";
        return should;
    }
</script>

<svelte:window bind:innerWidth />

<div class="shoplanding">
    {#if ready}
        <div
            class="relative flex size-full justify-center p-0 sm:p-12"
            in:fly={{ y: 64 }}
        >
            <!-- Background Image -->
            {#if backgroundImage != null}
                <div class="absolute inset-0">
                    <ResponsiveImage
                        image={backgroundImage}
                        className="size-full object-cover"
                    />
                </div>
            {/if}

            <div
                class="relative flex size-full max-w-screen-md items-stretch overflow-hidden bg-white sm:rounded-3xl sm:shadow-xl"
            >
                <section
                    class="flex size-full flex-col p-6 transition-[width]"
                    class:sm:w-[52.5%]={$isCoverImageVisible}
                    class:sm:w-full={!$isCoverImageVisible}
                >
                    <div
                        class="flex h-full max-h-full flex-1 flex-col items-center justify-between"
                    >
                        <header
                            class="flex flex-col items-center space-y-4 sm:max-w-[60%]"
                        >
                            {#if logo != null}
                                <div
                                    class="logo"
                                    class:wide={logo.width >= logo.height}
                                    class:high={logo.height > logo.width}
                                    style="--max-logo-size: {logo.maxSize}px;"
                                >
                                    <img
                                        src={logo.src}
                                        width={logo.width}
                                        height={logo.height}
                                        alt={logo.alt}
                                    />
                                </div>
                            {/if}

                            {#key $store.globalStepIdx}
                                <h1
                                    class="text-center text-xl font-bold sm:text-2xl"
                                    transition:slide={{ delay: 250 }}
                                >
                                    {#if isTitleVisible}
                                        {$step.title}
                                    {/if}
                                </h1>
                            {/key}
                        </header>

                        <div
                            class="flex w-full flex-1 items-stretch overflow-hidden py-3"
                        >
                            <div
                                class="w-0 transition-[margin-left] duration-300 ease-in-out"
                                style={`margin-left:-${$marginLeftOffset}%;`}
                            />
                            <Segments {segments} />
                        </div>

                        {#key $segment.id}
                            {#if $isProgressVisible}
                                <div
                                    class="w-full"
                                    in:fade
                                    out:fade={{ delay: 1000 }}
                                >
                                    {#if $segment.steps.length <= 6 || innerWidth >= 640}
                                        <ProgressBullets
                                            prize={$segmentConfig.prize}
                                            stepsCount={$segment.steps.length}
                                            currentStep={$stepIdx}
                                        />
                                    {:else}
                                        <ProgressPercentage
                                            prize={$segmentConfig.prize}
                                            stepsCount={$segment.steps.length}
                                            currentStep={$stepIdx}
                                        />
                                    {/if}
                                </div>
                            {/if}
                        {/key}
                    </div>
                </section>

                {#if $isCoverImageVisible}
                    <!-- Cover Image -->
                    <div
                        class={`relative hidden flex-1 ${coverImage != null ? "sm:block" : ""}`}
                        out:fade
                    >
                        <img
                            class="relative size-full object-cover"
                            src={coverImage.src}
                            alt={coverImage.alt}
                            width={coverImage.width}
                            height={coverImage.height}
                            loading="lazy"
                        />
                    </div>
                {/if}
            </div>
        </div>
    {:else}
        <div class="loading-dots-wrapper">
            <LoadingDots />
        </div>
    {/if}
</div>

<style>
    .shoplanding {
        width: 100%;
        height: 100%;
    }
    .loading-dots-wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
        align-items: center;
        justify-content: center;
    }
    .logo.wide {
        width: 100%;
        max-width: var(--max-logo-size);
    }

    .logo.high {
        height: 100%;
        max-height: var(--max-logo-size);
    }
</style>
